import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosClient from "../../libs/axiosClient";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import styles from "./FindAccount.module.scss";

function FindAccount() {
  const [selectedTab, setSelectedTab] = useState("consultation");
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { t, i18n } = useTranslation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [id, setId] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [foundId, setFoundId] = useState("");
  const [passwordSent, setPasswordSent] = useState(false);

  const handleHeaderTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  const handleTabChange = (tabIndex: number) => {
    setActiveTab(tabIndex);
    navigate(`/findAccount?tab=${tabIndex}`);
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tab = parseInt(query.get("tab") || "0", 10);
    setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage") || "en";
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(value));
  };

  const isFindIdFormValid = () => {
    return name.trim() !== "" && email.trim() !== "" && isEmailValid;
  };

  const isFindPasswordFormValid = () => {
    return id.trim() !== "" && email.trim() !== "" && isEmailValid;
  };
  
  const handleFindIdSubmit = async () => {
    if (!isFindIdFormValid()) return;

    try {
      const response = await axiosClient.post("/api/v1/auth/find-id", {
        name,
        email,
      });
      alert(`아이디 찾기 성공: ${response.data.msg}`);
      setFoundId(response.data.email);
    } catch (error: any) {
      console.error("Error finding ID:", error);
      alert(
        error.response?.data?.message || t("findAccount.findIdError")
      );
    }
  };
  
  const handleFindPasswordSubmit = async () => {
    if (!isFindPasswordFormValid()) return;
  
    try {
      const response = await axiosClient.post("/api/v1/auth/find-pw", {
        uid: id,
        name,
        email,
      });
      alert(t(`비밀번호 찾기 성공: ${response.data.msg}`));
      setPasswordSent(true);
    } catch (error: any) {
      console.error("Error finding password:", error);
      alert(
        error.response?.data?.detail || t("findAccount.findPasswordError")
      );
    }
  };

  return (
    <>
      <Header selectedTab={selectedTab} onTabChange={handleHeaderTabChange} />
      <div className={styles.container}>
        <div className={styles.tabHeader}>
          <div
            className={`${styles.tabItem} ${
              activeTab === 0 ? styles.active : ""
            }`}
            onClick={() => handleTabChange(0)}
          >
            {t("findAccount.findIdTab")}
          </div>
          <div
            className={`${styles.tabItem} ${
              activeTab === 1 ? styles.active : ""
            }`}
            onClick={() => handleTabChange(1)}
          >
            {t("findAccount.findPasswordTab")}
          </div>
        </div>
        <div className={styles.tabContent}>
          {activeTab === 0 && (
            <div>
              <h3>{t("findAccount.findIdTitle")}</h3>
              <div className={styles.formGroup}>
                <label>{t("findAccount.name")}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("findAccount.namePlaceholder")}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t("findAccount.email")}</label>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder={t("findAccount.emailPlaceholder")}
                />
                {!isEmailValid && (
                  <p className={styles.errorText}>
                    {t("findAccount.invalidEmail")}
                  </p>
                )}
              </div>
              <button
                className={`${styles.submitButton} ${
                  isFindIdFormValid() ? styles.active : ""
                }`}
                disabled={!isFindIdFormValid()}
                onClick={handleFindIdSubmit}
              >
                {t("findAccount.submit")}
              </button>
              {foundId && (
                <div className={styles.foundId}>
                  <strong>{t("findAccount.foundIdLabel")}</strong>
                  <p>{foundId}</p>
                </div>
              )}
            </div>
          )}
          {activeTab === 1 && (
            <div>
              <h3>{t("findAccount.findPasswordTitle")}</h3>
              <div className={styles.formGroup}>
                <label>{t("findAccount.id")}</label>
                <input
                  type="text"
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  placeholder={t("findAccount.idPlaceholder")}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t("findAccount.name")}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("findAccount.namePlaceholder")}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t("findAccount.email")}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder={t("findAccount.emailPlaceholder")}
                />
                {!isEmailValid && (
                  <p className={styles.errorText}>
                    {t("findAccount.invalidEmail")}
                  </p>
                )}
              </div>
              <button
                className={`${styles.submitButton} ${
                  isFindPasswordFormValid() ? styles.active : ""
                }`}
                disabled={!isFindPasswordFormValid()}
                onClick={handleFindPasswordSubmit}
              >
                {t("findAccount.submit")}
              </button>
              {passwordSent && (
              <div className={styles.passwordSentMessage}>
                {t("findAccount.passwordSentMessage")}
              </div>
            )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default FindAccount;
